/**
 * Time Entries hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { TimeEntry, CreateTimeEntryInput, UpdateTimeEntryInput } from '@aelita/shared';

interface TimeEntryFilters {
  page?: number;
  limit?: number;
  employeeId?: string;
  projectId?: string;
  taskId?: string;
  isBillable?: boolean;
  isApproved?: boolean;
  startDate?: string;
  endDate?: string;
}

interface TimeEntriesResponse {
  data: TimeEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  totals: {
    totalMinutes: number;
    totalHours: string;
    billableMinutes: number;
    billableHours: string;
  };
}

export function useTimeEntries(filters: TimeEntryFilters = {}, enabled = true) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  });

  return useQuery({
    queryKey: ['timeEntries', filters],
    enabled,
    placeholderData: undefined,
    queryFn: async () => {
      if (!enabled) throw new Error('Time entry query is disabled');
      const response = await api.get<TimeEntry[]>(`/time-entries?${params.toString()}`);
      return response as unknown as TimeEntriesResponse;
    },
  });
}

export function useTimeEntry(id: string | undefined) {
  return useQuery({
    queryKey: ['timeEntry', id],
    queryFn: async () => {
      const response = await api.get<TimeEntry>(`/time-entries/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTimeEntryInput) => {
      const response = await api.post<TimeEntry>('/time-entries', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
    },
  });
}

export function useUpdateTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTimeEntryInput }) => {
      const response = await api.put<TimeEntry>(`/time-entries/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntry', id] });
    },
  });
}

export function useDeleteTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/time-entries/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
    },
  });
}

export function useApproveTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<TimeEntry>(`/time-entries/${id}/approve`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntry', id] });
    },
  });
}

export function useApproveTimeEntriesBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (timeEntryIds: string[]) => {
      const response = await api.post<{ approved: number }>('/time-entries/approve-bulk', {
        timeEntryIds,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
    },
  });
}
